<template>
  <div class="pa-0 ma-0 fill-height dashboard-page" style="background-color:#ffffff">
      <v-container style="max-width: 1280px;" class="pt-6 pb-0">
        <dashboard-greeting
          :user-name="userDisplayName"
          :date="formattedDate"
        />
      </v-container>

      <!-- What's New: Tracey agents + Reports Marketplace -->
      <v-container
        id="whats-new-section"
        style="max-width: 1280px;"
        class="py-0"
        :class="{ 'whats-new-flash': highlightWhatsNew }"
      >
        <whats-new-panel />
      </v-container>

      <!-- Intelligence setup hero (before stats per iteration layout) -->
      <intelligence-setup-banner
        :visible="showBanner"
        :setup-progress="setupProgress"
        :configured-clusters-count="configuredClustersCount"
        :cluster-buttons="clusterButtons"
        @dismiss="dismissBanner"
        @continue-setup="showContinueSetupDialog"
        @cluster-click="handleClusterButtonClick"
      />

      <dashboard-stats-grid
        :stat-cards="dashboardStatGridComposable?.statCards?.value || []"
        compact
        @navigate="statCardNavigation"
      />

      <!-- ========== Main layout: activity column | sidebar ========== -->
      <v-container style="max-width: 1280px;">
        <v-row style="margin: 0 -12px;">
          <!-- Left Column - Main Content (2/3) -->
          <v-col cols="12" lg="8" style="padding: 0 12px;" :class="{ 'sections-container': activeTab === 'all' }">

            <!-- Navigation Tabs -->
            <dashboard-navigation-tabs
              v-model="activeTab"
              :tabs="navigationTabs"
              :tab-counts="tabCounts"
              :should-highlight="showTabHighlight"
              @tab-click="navTabNavigation"
            />

            <!-- Activity sections (order on "All" tab follows `orderedSections` from stat grid) -->
            <!-- Saved Search New Matches Section -->
            <saved-search-new-matches-section
              :matches="savedSearchNewMatchesList"
              :digest-loading="savedSearchNewMatchesLoading"
              :visible="isDashboardSectionVisible('new_matches')"
              :section-style="getSectionOrder('new_matches')"
              :page-size="defaultSectionPageSize"
              @add-to-pipeline="openAddToPipeline"
            />

            <!-- New Keyword Matches Section -->
            <keyword-matches-section
              :is-visible="isDashboardSectionVisible('keyword_matches')"
              :matches="keywordMatchesSectionComposable?.keywordMatches?.value || []"
              :is-loading="keywordMatchesSectionComposable?.loading?.value || false"
              :section-style="getSectionOrder('keyword_matches')"
              :items-per-page="defaultSectionPageSize"
              @add-to-pipeline="openAddToPipeline"
            />

            <!-- Pipeline Expiring Contracts Section -->
            <pipeline-expiring-contracts-section
              :is-visible="isDashboardSectionVisible('expiring')"
              :contracts="pipelineExpiringContracts"
              :is-loading="pipelineExpiringContractsLoading"
              :section-style="getSectionOrder('expiring')"
              :items-per-page="defaultSectionPageSize"
              @remove-from-pipeline="removeFromPipeline"
            />

            <!-- Competitor Activity Section -->
            <competitor-activity-section
              :is-visible="isDashboardSectionVisible('competitors_activity')"
              :activities="competitorActivityList"
              :is-loading="competitorActivityLoading"
              :section-style="getSectionOrder('competitors_activity')"
              :items-per-page="defaultSectionPageSize"
              @analyze="handleAnalyzeCompetitorActivity"
            />

            <!-- Buyer Activity Section -->
            <buyer-activity-section
              :is-visible="isDashboardSectionVisible('buyer_activity')"
              :activities="trackedBuyerActivity"
              :is-loading="loadingDashboardStatGrid"
              :section-style="getSectionOrder('buyer_activity')"
              :items-per-page="defaultSectionPageSize"
              @add-to-pipeline="openAddToPipeline"
            />

            <!-- Industry Matches Section -->
            <industry-matches-section
              :is-visible="isDashboardSectionVisible('industry_matches')"
              :matches="industryMatchesSectionComposable?.industryMatches?.value || []"
              :is-loading="loadingDashboardStatGrid"
              :section-style="getSectionOrder('industry_matches')"
              :items-per-page="defaultSectionPageSize"
            />

            <!-- New Entrants Section -->
            <new-entrants-section
              :is-visible="isDashboardSectionVisible('new_entrants')"
              :entrants="newEntrantsSectionComposable?.newEntrants?.value || []"
              :is-loading="loadingDashboardStatGrid"
              :section-style="getSectionOrder('new_entrants')"
              :items-per-page="defaultSectionPageSize"
            />

            <!-- Competitor Expiring Contracts Section -->
            <competitor-expiring-contracts-section
              :is-visible="isDashboardSectionVisible('competitor_expiring')"
              :contracts="competitorExpiringContracts"
              :is-loading="competitorExpiringContractsLoading"
              :section-style="getSectionOrder('competitor_expiring')"
              :items-per-page="defaultSectionPageSize"
              @add-to-pipeline="openAddToPipeline"
            />

            <!-- Panel Intelligence Section -->
            <panel-intelligence-section
              :is-visible="isDashboardSectionVisible('panel_intelligence')"
              :panel-intelligence="panelIntelligenceSectionComposable?.panelIntelligence?.value || {}"
              :is-loading="loadingDashboardStatGrid"
              :section-style="getSectionOrder('panel_intelligence')"
              :items-per-page="defaultSectionPageSize"
            />
          </v-col>

          <!-- Right column: sidebar (1/3) -->
          <v-col cols="12" lg="4" style="padding: 0 12px;">
            <market-signals-panel
              :market-signals="marketSignalPanelComposable?.marketSignals?.value || []"
              :loading="marketSignalPanelComposable?.loading?.value || false"
              :items-per-page="marketSignalPageSize"
              :format-currency="marketSignalPanelComposable?.formatCurrency || (() => '$0')"
              :highlight="marketSignalPanelComposable?.showHighlight?.value || false"
            />

            <custom-reports-panel
              :reports="reports"
              :loading="loadingReports"
              :is-visible="true"
              :section-style="''"
              :items-per-page="reportPageSize"
              @open-in-new-tab="openInNewTab"
            />

            <!-- Intelligence Clusters Panel -->
            <intelligence-clusters-panel
              :clusters="intelligenceClusters"
              :loading="loadingClusters"
              :current-page="clustersCurrentPage"
              :page-size="clustersPageSize"
              @page-change="handleClustersPageChange"
              @cluster-click="handleClusterNavigation"
            />
          </v-col>
        </v-row>
      </v-container>

    <!-- ========== Global overlays: snackbar + dialogs ========== -->
    <!-- Snackbar (pipeline / intelligence / API feedback) -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- ListManager: add tenders to pipeline -->
    <v-dialog v-model="showListManager" max-width="1000px" scrollable>
      <list-manager 
        v-show="showListManager" 
        :tenders="tendersToAdd" 
        :initialSelectedLists="[]"
        :changed="0"
        @hide-list-manager="showListManager = false" 
        @pipeline-updated="handlePipelineUpdated">
      </list-manager>
    </v-dialog>

    <!-- Intelligence setup: instruction + entity selector dialogs -->
    <v-dialog v-model="showSetupDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6 font-weight-bold">{{ getContinueSetupInstruction().title }}</span>
          <v-btn icon @click="showSetupDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="mb-4">
            <p class="text-body-1 mb-3" style="color: #1f2937;">
              {{ getContinueSetupInstruction().text }}
            </p>
            <v-alert type="info" outlined class="mb-0">
              <div class="text-body-2">
                <strong>Why this matters:</strong> {{ getContinueSetupInstruction().importance }}
              </div>
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showSetupDialog = false">Cancel</v-btn>
          <v-btn color="primary" dark @click="handleContinueSetup">
            Continue
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cluster Selector Dialog -->
    <v-dialog v-model="showClusterSelector" max-width="800px" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6 font-weight-bold">
            Add entities to {{ currentClusterType ? currentClusterType.name : 'Cluster' }} 
          </span>
          <v-btn icon @click="showClusterSelector = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="py-3">
          <cluster-entity-selector
            v-if="currentClusterType && currentClusterType.entityType === 'publisher' && currentClusterType.clusterId"
            :cluster-id="currentClusterType.clusterId"
            @entities-added="onEntitiesAdded"
            @success="onSelectorSuccess"
            @error="onSelectorError"
          />
          <cluster-supplier-selector
            v-else-if="currentClusterType && currentClusterType.entityType === 'supplier' && currentClusterType.clusterId"
            :cluster-id="currentClusterType.clusterId"
            @entities-added="onEntitiesAdded"
            @success="onSelectorSuccess"
            @error="onSelectorError"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Dashboard: shared daily digest + `useDashboardStatGrid` / section composables,
 * intelligence setup (`useIntelligenceSetup`), tabs, sidebar panels, and dialogs.
 */
// --- API ---
import { fetchStoryboards, updatePipelines } from "../components/APIManager.js";
// --- Components (alphabetical) ---
import BuyerActivitySection from "../components/dashboard/BuyerActivitySection.vue";
import ClusterEntitySelector from "../components/ClusterEntitySelector.vue";
import ClusterSupplierSelector from "../components/ClusterSupplierSelector.vue";
import CompetitorActivitySection from "../components/dashboard/CompetitorActivitySection.vue";
import CompetitorExpiringContractsSection from "../components/dashboard/CompetitorExpiringContractsSection.vue";
import CustomReportsPanel from "../components/dashboard/CustomReportsPanel.vue";
import DashboardGreeting from "../components/dashboard/DashboardGreeting.vue";
import DashboardNavigationTabs from "../components/DashboardNavigationTabs.vue";
import DashboardStatsGrid from "../components/DashboardStatsGrid.vue";
import IndustryMatchesSection from "../components/dashboard/IndustryMatchesSection.vue";
import IntelligenceClustersPanel from "../components/IntelligenceClustersPanel.vue";
import IntelligenceSetupBanner from "../components/IntelligenceSetupBanner.vue";
import KeywordMatchesSection from "../components/dashboard/KeywordMatchesSection.vue";
import ListManager from "../components/ListManager.vue";
import MarketSignalsPanel from "../components/MarketSignalsPanel.vue";
import NewEntrantsSection from "../components/dashboard/NewEntrantsSection.vue";
import PanelIntelligenceSection from "../components/dashboard/PanelIntelligenceSection.vue";
import PipelineExpiringContractsSection from "../components/dashboard/PipelineExpiringContractsSection.vue";
import SavedSearchNewMatchesSection from "../components/dashboard/SavedSearchNewMatchesSection.vue";
import WhatsNewPanel from "../components/dashboard/WhatsNewPanel.vue";
// --- Composables (alphabetical) ---
import { useCompetitionExpiringContractsSection } from "../composables/useCompetitionExpiringContractsSection.js";
import { useCompetitorActivity } from "../composables/useCompetitorActivity.js";
import { useDailyDigest } from "../composables/useDailyDigest.js";
import { useDashboardStatGrid } from "../composables/useDashboardStatGrid.js";
import { useIndustryMatchesSection } from "../composables/useIndustryMatchesSection.js";
import { useIntelligenceSetup } from "../composables/useIntelligenceSetup.js";
import { useKeywordMatchesSection } from "../composables/useKeywordMatchesSection.js";
import { useMarketSignalPanel } from "../composables/useMarketSignalPanel.js";
import { useNewEntrantsSection } from "../composables/useNewEntrantsSection.js";
import { usePanelIntelligence } from "../composables/usePanelIntelligence.js";
import { usePipelineExpiringContractsSection } from "../composables/usePipelineExpiringContractsSection.js";
import { useSavedSearchNewMatches } from "../composables/useSavedSearchNewMatches.js";
import { APP_HEADER_HEIGHT } from "../components/layout/navigationDrawerLayout.js";
import { bus } from "../main.js";

/**
 * Nav tab key → dashboard section keys (same ids as `getSectionOrder(...)` / `orderedSections`).
 * Tab badge totals sum counts for these sections; `isDashboardSectionVisible` uses this for `:visible`.
 * The `all` tab is implicit: every section is shown; it does not aggregate counts.
 */
const NAVTAB_GROUPING = Object.freeze({
  new_matches: ['new_matches'],
  expiring: ['expiring'],
  competitors_activity: ['competitors_activity', 'competitor_expiring'],
  buyer_activity: ['keyword_matches', 'buyer_activity'],
  industry_activity: ['industry_matches', 'new_entrants'],
  panels: ['panel_intelligence']
});

export default {
  name: 'Dashboard',

  components: {
    BuyerActivitySection,
    ClusterEntitySelector,
    ClusterSupplierSelector,
    CompetitorActivitySection,
    CompetitorExpiringContractsSection,
    CustomReportsPanel,
    DashboardGreeting,
    DashboardNavigationTabs,
    DashboardStatsGrid,
    IndustryMatchesSection,
    IntelligenceClustersPanel,
    IntelligenceSetupBanner,
    KeywordMatchesSection,
    ListManager,
    MarketSignalsPanel,
    NewEntrantsSection,
    PanelIntelligenceSection,
    PipelineExpiringContractsSection,
    SavedSearchNewMatchesSection,
    WhatsNewPanel,
  },

  data() {
    return {
      // --- Tabs (reorder `navigationTabs` to change tab order) ---
      activeTab: 'all',
      navigationTabs: [
        { key: 'all', label: 'All Activity' },
        { key: 'new_matches', label: 'New Matches' },
        { key: 'expiring', label: 'Expiring Soon' },
        { key: 'competitors_activity', label: 'Competitors Activity' },
        { key: 'buyer_activity', label: 'Buyers Activity' },
        { key: 'industry_activity', label: 'Industry Activity' },
        { key: 'panels', label: 'Panels' }
      ],
      showTabHighlight: false,

      // --- Composables (all set in `created`, alphabetical by key) ---
      competitionExpiringContractsComposable: null,
      competitorActivityComposable: null,
      dailyDigestComposable: null,
      dashboardStatGridComposable: null,
      industryMatchesSectionComposable: null,
      intelligenceSetupApi: null,
      keywordMatchesSectionComposable: null,
      marketSignalPanelComposable: null,
      newEntrantsSectionComposable: null,
      panelIntelligenceSectionComposable: null,
      pipelineExpiringContractsComposable: null,
      savedSearchNewMatchesComposable: null,

      // --- Dashboard snapshot (synced from `useDashboardStatGrid` in `created`) ---
      dashboardStatGrid: {
        summary: {},
        expiring_contracts: [],
        competitor_activity: [],
        competitor_expiring_contracts: [],
        buyer_activity: [],
        market_patterns: [],
        new_matches: [],
        industry_matches: [],
        new_entrants: [],
        panel_intelligence: {
          competitors_recently_joined: [],
          suggested_panels: [],
          newly_created_panels: []
        }
      },
      defaultSectionPageSize: 10,
      loadingDashboardStatGrid: false,

      // --- Reports & sidebar pagination ---
      clustersCurrentPage: 1,
      clustersPageSize: 10,
      loadingReports: false,
      marketSignalPageSize: 3,
      reportCurrentPage: 1,
      reportPageSize: 10,
      reports: [],

      // --- Snackbar & add-to-pipeline dialog ---
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      showListManager: false,
      tendersToAdd: [],

      // Brief highlight when the user arrives via the top-bar "What's New" entry.
      highlightWhatsNew: false
    };
  },

  computed: {
    // --- Presentation ---
    formattedDate() {
      const today = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return today.toLocaleDateString('en-US', options);
    },

    userDisplayName() {
      return this.dailyDigestComposable?.dailyDigest?.value?.user?.name || 'there';
    },

    scrollOffset() {
      // App header height + content padding below header
      return APP_HEADER_HEIGHT + 12;
    },

    // --- Digest / dashboard composable bridges ---
    competitorExpiringContracts() {
      if (!this.competitionExpiringContractsComposable) return [];
      return this.competitionExpiringContractsComposable.competitorExpiringContracts?.value || [];
    },

    competitorExpiringContractsLoading() {
      if (!this.competitionExpiringContractsComposable) return false;
      return this.competitionExpiringContractsComposable.loading?.value || false;
    },

    pipelineExpiringContracts() {
      if (!this.pipelineExpiringContractsComposable) return [];
      return this.pipelineExpiringContractsComposable.pipelineExpiringContracts?.value || [];
    },

    pipelineExpiringContractsLoading() {
      if (!this.pipelineExpiringContractsComposable) return false;
      return this.pipelineExpiringContractsComposable.loading?.value || false;
    },

    savedSearchNewMatchesList() {
      if (!this.savedSearchNewMatchesComposable) return [];
      return this.savedSearchNewMatchesComposable.savedSearchNewMatches?.value || [];
    },

    savedSearchNewMatchesLoading() {
      if (!this.savedSearchNewMatchesComposable) return false;
      return this.savedSearchNewMatchesComposable.loading?.value || false;
    },

    competitorActivityList() {
      if (!this.competitorActivityComposable) return [];
      return this.competitorActivityComposable.competitorActivity?.value || [];
    },

    competitorActivityLoading() {
      if (!this.competitorActivityComposable) return false;
      return this.competitorActivityComposable.loading?.value || false;
    },

    /**
     * Per-section row counts (digest-backed), aligned with props passed into each dashboard section.
     * Keys must match `NAVTAB_GROUPING` section ids and `getSectionOrder(...)`.
     */
    dashboardSectionCounts() {
      const grid = this.dashboardStatGrid || {};
      return {
        new_matches: this.savedSearchNewMatchesList.length,
        expiring: this.pipelineExpiringContracts.length,
        competitors_activity: this.competitorActivityList.length,
        competitor_expiring: this.competitorExpiringContracts.length,
        keyword_matches: (this.keywordMatchesSectionComposable?.keywordMatches?.value || []).length,
        buyer_activity: this.trackedBuyerActivity.length,
        industry_matches: (this.industryMatchesSectionComposable?.industryMatches?.value || []).length,
        new_entrants: (this.newEntrantsSectionComposable?.newEntrants?.value || []).length,
        panel_intelligence: this.dashboardStatGridComposable?.panelIntelligenceCount?.value ?? 0
      };
    },

    tabCounts() {
      const zero = {
        all: 0,
        new_matches: 0,
        expiring: 0,
        competitors_activity: 0,
        buyer_activity: 0,
        panels: 0,
        industry_activity: 0
      };
      if (!this.dashboardStatGridComposable) return { ...zero };

      const counts = this.dashboardSectionCounts;
      const out = { ...zero };
      Object.keys(NAVTAB_GROUPING).forEach((tab) => {
        const sections = NAVTAB_GROUPING[tab];
        out[tab] = sections.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
      });
      out.all = 0;
      return out;
    },

    trackedBuyerActivity() {
      if (!this.dashboardStatGridComposable) return [];
      return this.dashboardStatGridComposable.trackedBuyerActivity?.value || [];
    },

    // --- Intelligence setup (`useIntelligenceSetup`) ---
    clusterButtons() {
      const api = this.intelligenceSetupApi;
      return api?.clusterButtons?.value ?? [];
    },

    configuredClustersCount() {
      const api = this.intelligenceSetupApi;
      return api?.configuredClustersCount?.value ?? 0;
    },

    currentClusterType: {
      get() {
        return this.intelligenceSetupApi?.currentClusterType?.value ?? null;
      },
      set(v) {
        if (this.intelligenceSetupApi) {
          this.intelligenceSetupApi.currentClusterType.value = v;
        }
      }
    },

    intelligenceClusters() {
      const api = this.intelligenceSetupApi;
      return api?.intelligenceClusters?.value ?? [];
    },

    loadingClusters() {
      const api = this.intelligenceSetupApi;
      return api?.loadingClusters?.value ?? false;
    },

    setupComplete() {
      const api = this.intelligenceSetupApi;
      return api?.setupComplete?.value ?? false;
    },

    setupProgress() {
      const api = this.intelligenceSetupApi;
      return api?.setupProgress?.value ?? 0;
    },

    showBanner() {
      const api = this.intelligenceSetupApi;
      return api?.showBanner?.value ?? false;
    },

    showClusterSelector: {
      get() {
        return this.intelligenceSetupApi?.showClusterSelector?.value ?? false;
      },
      set(v) {
        if (this.intelligenceSetupApi) {
          this.intelligenceSetupApi.showClusterSelector.value = v;
        }
      }
    },

    showSetupDialog: {
      get() {
        return this.intelligenceSetupApi?.showSetupDialog?.value ?? false;
      },
      set(v) {
        if (this.intelligenceSetupApi) {
          this.intelligenceSetupApi.showSetupDialog.value = v;
        }
      }
    }
  },

  created() {
    // --- Shared daily digest ---
    const dailyDigestComposable = useDailyDigest();
    this.dailyDigestComposable = dailyDigestComposable;

    const digestOpts = {
      dailyDigest: dailyDigestComposable.dailyDigest,
      loading: dailyDigestComposable.loading
    };

    // --- Main stat grid (depends on digest `load`) ---
    const dashboardStatGridComposable = useDashboardStatGrid({
      dailyDigest: dailyDigestComposable.dailyDigest,
      loading: dailyDigestComposable.loading,
      load: dailyDigestComposable.load
    });
    this.dashboardStatGridComposable = dashboardStatGridComposable;

    // --- Other digest-backed composables (alphabetical) ---
    this.competitionExpiringContractsComposable = useCompetitionExpiringContractsSection(digestOpts);
    this.competitorActivityComposable = useCompetitorActivity(digestOpts);
    this.industryMatchesSectionComposable = useIndustryMatchesSection(digestOpts);
    this.keywordMatchesSectionComposable = useKeywordMatchesSection(digestOpts);
    this.marketSignalPanelComposable = useMarketSignalPanel(digestOpts);
    this.newEntrantsSectionComposable = useNewEntrantsSection(digestOpts);
    this.panelIntelligenceSectionComposable = usePanelIntelligence(digestOpts);
    this.pipelineExpiringContractsComposable = usePipelineExpiringContractsSection(digestOpts);
    this.savedSearchNewMatchesComposable = useSavedSearchNewMatches(digestOpts);

    // --- Intelligence setup (refresh hook = reload dashboard digest) ---
    const showSnackbar = (message, color) => {
      this.snackbar.message = message;
      this.snackbar.color = color || 'success';
      this.snackbar.show = true;
    };
    this.intelligenceSetupApi = useIntelligenceSetup(
      () => this.loadDashboardStatGrid(),
      showSnackbar
    );
    this.intelligenceSetupApi.resetBannerDismissal();

    // --- Sync stat grid into Options API `data` (legacy child props / mutations) ---
    this.dashboardStatGrid = dashboardStatGridComposable.dashboardStatGrid.value;
    this.loadingDashboardStatGrid = dashboardStatGridComposable.loadingDashboardStatGrid.value;
    this.defaultSectionPageSize = dashboardStatGridComposable.defaultSectionPageSize.value;

    this.$watch(() => dashboardStatGridComposable.dashboardStatGrid.value, (newVal) => {
      this.dashboardStatGrid = newVal;
    }, { deep: true });

    this.$watch(() => dashboardStatGridComposable.loadingDashboardStatGrid.value, (newVal) => {
      this.loadingDashboardStatGrid = newVal;
    });

    this.$watch(() => dashboardStatGridComposable.defaultSectionPageSize.value, (newVal) => {
      this.defaultSectionPageSize = newVal;
    });
  },

  async mounted() {
    this.intelligenceSetupApi.resetBannerDismissal();
    await this.loadDashboardStatGrid();
    await this.loadReports();
    await this.intelligenceSetupApi.checkIntelligenceSetup();
    this.syncIntelligenceSetupToShell();
    bus.$on('open-intelligence-setup', this.onOpenIntelligenceSetupFromShell);
    bus.$on('scroll-to-whats-new', this.scrollToWhatsNew);

    // Arriving from the top-bar "What's New" entry on another page: the query
    // flag survives the route change, so scroll once the section has rendered.
    if (this.$route.query.section === 'whats-new') {
      this.$nextTick(() => setTimeout(this.scrollToWhatsNew, 300));
    }
  },

  beforeDestroy() {
    bus.$off('open-intelligence-setup', this.onOpenIntelligenceSetupFromShell);
    bus.$off('scroll-to-whats-new', this.scrollToWhatsNew);
  },

  watch: {
    setupProgress() {
      this.syncIntelligenceSetupToShell();
    },
    showBanner() {
      this.syncIntelligenceSetupToShell();
    },
  },

  methods: {
    syncIntelligenceSetupToShell() {
      bus.$emit('intelligence-setup-update', {
        progress: this.setupProgress,
        showCard: this.setupProgress < 100,
      });
    },

    scrollToWhatsNew() {
      this.$nextTick(() => {
        const el = document.getElementById('whats-new-section');
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - this.scrollOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        // Flash the section briefly so the eye lands on it.
        this.highlightWhatsNew = true;
        setTimeout(() => { this.highlightWhatsNew = false; }, 1600);
      });
    },

    onOpenIntelligenceSetupFromShell() {
      if (this.setupProgress < 100) {
        this.showContinueSetupDialog();
        return;
      }
      const banner = document.getElementById('intelligence-setup-banner');
      if (banner) {
        banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    // --- Navigation (stat grid + tabs) ---
    /**
     * Whether a dashboard section should render for the current nav tab.
     * Uses `NAVTAB_GROUPING`; `all` shows every section.
     */
    isDashboardSectionVisible(sectionKey) {
      if (this.activeTab === 'all') return true;
      const sections = NAVTAB_GROUPING[this.activeTab];
      return Array.isArray(sections) && sections.includes(sectionKey);
    },

    statCardNavigation(navTabKey) {
      if (!navTabKey) return;
      
      // Handle Market Signals separately (it's in the sidebar, not a tab)
      if (navTabKey === 'market_signals') {
        // Switch to All Activity tab
        this.activeTab = 'all';
        
        // Trigger highlight effect
        if (this.marketSignalPanelComposable) {
          this.marketSignalPanelComposable.triggerHighlight();
        }
        
        // Scroll to Market Signals panel
        this.$nextTick(() => {
          const panelElement = document.getElementById('market-signals-panel');
          if (!panelElement) return;
          
          // Only scroll if banner is visible, or if we're already below the panel
          if (this.showBanner) {
            // Banner is visible: calculate offset including banner height
            const bannerElement = document.getElementById('intelligence-setup-banner');
            const bannerHeight = bannerElement ? bannerElement.offsetHeight : 0;
            const elementPosition = panelElement.getBoundingClientRect().top + window.pageYOffset;
            // Offset: app header + padding + banner height
            const offset = this.scrollOffset + bannerHeight;
            const scrollPosition = elementPosition - offset;
            
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          } else {
            // Banner not visible: only scroll if we're already below the panel
            const panelRect = panelElement.getBoundingClientRect();
            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const panelTop = panelRect.top + currentScrollTop;
            
            // Only scroll if panel is above current viewport
            if (panelTop < currentScrollTop) {
              const elementPosition = panelTop;
              // Offset: app header + padding
              const offset = this.scrollOffset;
              const scrollPosition = elementPosition - offset;
              
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
            }
          }
        });
        return;
      }
      
      // Check if the navTabKey exists in navigationTabs
      const tabExists = this.navigationTabs.some(tab => tab.key === navTabKey);
      
      if (tabExists) {
        // Set activeTab to the tab key directly
        this.activeTab = navTabKey;

        // Trigger highlight effect when navigating from stat grid
        this.showTabHighlight = true;
        setTimeout(() => {
          this.showTabHighlight = false;
        }, 1500); // Match animation duration
        
        // Scroll to the navigation tabs section, positioning it above the viewport
        // so users can see the nav tab to understand where they are
        this.$nextTick(() => {
          const tabsElement = document.getElementById('dashboard-navigation-tabs');
          if (!tabsElement) return;
          
          // Only scroll if banner is visible, or if we're already below the nav tabs
          if (this.showBanner) {
            // Banner is visible: calculate offset including banner height
            const bannerElement = document.getElementById('intelligence-setup-banner');
            const bannerHeight = bannerElement ? bannerElement.offsetHeight : 0;
            const elementPosition = tabsElement.getBoundingClientRect().top + window.pageYOffset;
            // Offset: app header + padding + banner height
            const offset = this.scrollOffset + bannerHeight;
            const scrollPosition = elementPosition - offset;
            
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          } else {
            // Banner not visible: only scroll if we're already below the nav tabs
            const tabsRect = tabsElement.getBoundingClientRect();
            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const tabsTop = tabsRect.top + currentScrollTop;
            
            // Only scroll if nav tabs are above current viewport
            if (tabsTop < currentScrollTop) {
              const elementPosition = tabsTop;
              // Offset: app header + padding
              const offset = this.scrollOffset;
              const scrollPosition = elementPosition - offset;
              
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
            }
          }
        });
      }
    },

    navTabNavigation(navTabKey) {
      if (!navTabKey) return;
      const tabExists = this.navigationTabs.some(tab => tab.key === navTabKey);
      if (tabExists) {
        this.activeTab = navTabKey;
      }
    },

    // --- Dashboard digest & reports ---
    async loadDashboardStatGrid() {
      // Use composable's loadDashboardStatGrid method
      if (this.dashboardStatGridComposable) {
        await this.dashboardStatGridComposable.loadDashboardStatGrid();
      }
    },

    async loadReports() {
      this.loadingReports = true;
      try {
        const data = await fetchStoryboards();
        this.reports = data.storyboards || [];
        // Reset to first page when reports are loaded
        this.reportCurrentPage = 1;
      } catch (error) {
        //////////////////////console.error('Error loading reports:', error);
      } finally {
        this.loadingReports = false;
      }
    },

    handleReportPageChange(page) {
      this.reportCurrentPage = page;
    },

    // --- Intelligence clusters panel (sidebar pagination) ---
    handleClustersPageChange(page) {
      this.clustersCurrentPage = page;
    },

    handleClusterNavigation(clusterId) {
      this.$router.push(`/cluster/${clusterId}`);
    },

    // --- Pipeline & ListManager ---
    async removeFromPipeline(contract) {
      // Set loading state on the contract
      this.$set(contract, 'removing', true);
      
      try {
        // Call updatePipelines with empty array to remove from all pipelines
        await updatePipelines(contract.tender_id, []);
        
        // Remove the contract from the local data
        const index = this.dashboardStatGrid.expiring_contracts.findIndex(
          c => c.tender_id === contract.tender_id
        );
        if (index !== -1) {
          this.dashboardStatGrid.expiring_contracts.splice(index, 1);
        }
        
        // Show success message
        this.snackbar.message = 'Contract removed from pipeline successfully';
        this.snackbar.color = 'success';
        this.snackbar.show = true;
      } catch (error) {
        ////////console.error('Error removing contract from pipeline:', error);
        // Show error message
        this.snackbar.message = 'Failed to remove contract from pipeline. Please try again.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
      } finally {
        this.$set(contract, 'removing', false);
      }
    },

    handleAnalyzeCompetitorActivity(activity) {
      // Navigate to tender page for analysis
      this.$router.push(`/tender/${activity.tender_id}`);
    },

    openAddToPipeline(item) {
      // Extract tender_id from the item (could be contract, match, or activity)
      // Only use tender_id field - don't fall back to id or _id as those might be different entity IDs
      const tenderId = item.tender_id;
      
      if (!tenderId) {
        //////console.error('Unable to add to pipeline: Tender ID not found in item:', item);
        this.snackbar.message = 'Unable to add to pipeline: Tender ID not found';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
        return;
      }
      
      // Ensure tender_id is a valid integer
      const parsedTenderId = parseInt(tenderId);
      if (isNaN(parsedTenderId) || parsedTenderId <= 0) {
        //////console.error('Invalid tender_id format:', tenderId);
        this.snackbar.message = 'Invalid tender ID format';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
        return;
      }
      
      // Set up the tenders array for ListManager
      // ListManager expects an array of tender objects or IDs
      this.tendersToAdd = [{
        id: parsedTenderId,
        title: item.title || 'Contract'
      }];
      
      // Open the ListManager dialog
      this.showListManager = true;
    },

    handlePipelineUpdated(event) {
      // Handle the pipeline updated event from ListManager
      // Optionally refresh dashboard stat grid data or show a success message
      this.snackbar.message = 'Contract added to pipeline successfully';
      this.snackbar.color = 'success';
      this.snackbar.show = true;
    },

    // --- Section layout (tab "All" ordering) ---
    getSectionOrder(sectionKey) {
      if (this.activeTab !== 'all') {
        return 'border: 1px solid #e5e7eb; background-color: white; border-radius: 8px; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);';
      }

      const orderedSections = this.dashboardStatGridComposable?.orderedSections?.value || [];
      const order = orderedSections.indexOf(sectionKey);
      const baseStyle = 'border: 1px solid #e5e7eb; background-color: white; border-radius: 8px; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);';

      if (order !== -1) {
        return `${baseStyle} order: ${order + 1};`;
      }

      return baseStyle;
    },

    // --- Routing ---
    openInNewTab(route) {
      const routeData = this.$router.resolve(route);
      window.open(routeData.href, '_blank');
    },

    // --- Intelligence setup (`useIntelligenceSetup` delegates) ---
    dismissBanner() {
      this.intelligenceSetupApi.dismissBanner();
    },

    showContinueSetupDialog() {
      this.intelligenceSetupApi.showContinueSetupDialog();
    },

    handleClusterButtonClick(clusterType) {
      this.intelligenceSetupApi.handleClusterButtonClick(clusterType);
    },

    handleContinueSetup() {
      this.intelligenceSetupApi.handleContinueSetup();
    },

    onEntitiesAdded(entities) {
      this.intelligenceSetupApi.onEntitiesAdded(entities);
    },

    onSelectorSuccess(message) {
      this.intelligenceSetupApi.onSelectorSuccess(message);
    },

    onSelectorError(message) {
      this.intelligenceSetupApi.onSelectorError(message);
    },

    getContinueSetupInstruction() {
      return this.intelligenceSetupApi.getContinueSetupInstruction();
    },

    // --- Date / display helpers (unused in this template) ---
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return String(dateStr);
      }
    },

    getDaysColor(days) {
      if (days <= 30) return '#f59e0b';
      if (days <= 90) return '#3b82f6';
      return '#10b981';
    },

    getContractCardStyle(contract) {
      // Handle regular expiring contracts (urgent_3mo, medium_6mo)
      if (contract.urgency === 'urgent_3mo' || contract.urgency === '90_days') {
        return 'cursor: pointer; background-color: #fff9e6; border-left: 4px solid #f59e0b; border-radius: 0 4px 4px 0;';
      } else if (contract.urgency === 'medium_6mo' || contract.urgency === '120_days') {
        return 'cursor: pointer; background-color: #f0f9ff; border-left: 4px solid #3b82f6; border-radius: 0 4px 4px 0;';
      }
      return 'cursor: pointer; background-color: #f9fafb; border-left: 4px solid #9e9e9e; border-radius: 0 4px 4px 0;';
    },


    getValueChange(current, average) {
      if (!average || average === 0) return 0;
      return Math.round(((current - average) / average) * 100);
    },

    getClosingDateColor(closingDate) {
      const days = this.getDaysUntilClose(closingDate);
      if (days <= 7) return '#dc2626';
      if (days <= 14) return '#d97706';
      return '#059669';
    },

    getDaysUntilClose(closingDate) {
      if (!closingDate) return 0;
      try {
        const closeDate = new Date(closingDate);
        const today = new Date();
        const diffTime = closeDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      } catch (e) {
        return 0;
      }
    }
  }
};
</script>

<style scoped>
.sections-container {
  display: flex;
  flex-direction: column;
}

/* --- Legacy / shared utility classes --- */
.contract-card {
  transition: all 0.2s ease;
}

.contract-card:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

.hover-red-lighten-5:hover {
  background-color: #ffebee !important;
}

.hover-green-lighten-5:hover {
  background-color: #e8f5e9 !important;
}

.hover-grey-lighten-5:hover {
  background-color: #fafafa !important;
}

.hover-grey-lighten-4:hover {
  background-color: #f5f5f5 !important;
}

.hover-blue-lighten-5:hover {
  background-color: #dbeafe !important;
}

.hover-orange-lighten-5:hover {
  background-color: #fff7ed !important;
}

.border-top {
  border-top: 1px solid #e5e7eb;
}

.border-b {
  border-bottom: 1px solid #e5e7eb;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.section-card {
  border: 1px solid #e5e7eb;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

/* Brief attention flash when navigating in via the top-bar "What's New". */
.whats-new-flash >>> .whats-new {
  animation: whats-new-flash 1.6s ease-out;
}

@keyframes whats-new-flash {
  0% { box-shadow: 0 0 0 0 rgba(255, 20, 99, 0.5); }
  30% { box-shadow: 0 0 0 4px rgba(255, 20, 99, 0.28); }
  100% { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1); }
}
</style>
